import React, { useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { Link } from 'react-router-dom';
import { Grid, Header, Icon, Popup, Portal, Ref } from 'semantic-ui-react';
import { PhoneUtil, StringUtils } from 'utils';
import "./index.scss"
import ButtonLink from 'dyl-components/atoms/ButtonLink';
import { DragOverlay, useDraggable } from '@dnd-kit/core';
import DragHandle from 'dyl-components/atoms/DragHandle';
import DraggableCall from '../DraggableCall';
import RestrictedView from '../Restricted';
import { USER_PERMISSIONS, PRODUCT_PERMISSIONS } from '../Restricted/PERMISSIONS';
import OfficeViewCallerDetails from '../OfficeViewCallerDetails';
import BannedIcon from '../BannedIcon';

const OfficeViewMiddleHeader = ({
    person,
    onRemovePerson,
    onEmailClick,
    onTaskClick,
    onEventClick,
    onHotlistClick,
    isInHotlist,
    hasIntegration = false,
    logActivityForm,
    isLogActivityModalOpen,
    setIsLogActivityModalOpen,
    isCallActivityFormOpen,
    setIsCallActivityFormOpen,
    callState,
    unknown,
    onNewContactClick,
    onClickToDial,
    isPhoneSystemSupported,
    isInternalCall,
    currentCall,
    currentTime,
    isCallHeld,
    userPhoneCreds,
    currentCallUUID,
    onDirectCustomerClick
}) => {
    const [isEmailDisabled, setIsEmailDisabled] = useState(false);
    useEffect(() => {
        const isEmailDisabledAux = person?.contact_id ? (
            !person?.contact_details?.email
        ) : (
            !person?.account_details?.email
        )
        setIsEmailDisabled(isEmailDisabledAux);
    }, [hasIntegration, person])

    const isCallActive = callState === "active";
    const isCallRinging = callState === "ringing";
    const isCallNotIdle = isCallActive || isCallRinging;
    const isUnknown = unknown.unknown;
    const contactLeg = PhoneUtil.contactLeg(currentCall, userPhoneCreds);
    const phone = contactLeg?.phone;
    const recordDetails = person?.contact_details || person?.account_details;
    const hasPhone = (recordDetails)?.phone?.length > 0;
    const callSuspended = recordDetails?.call_suspended || recordDetails?.phone?.find(phone => phone.main)?.call_suspended;
    const direct_customer = person?.contact_details?.direct_customer;
    const { isDragging, setNodeRef, attributes, listeners, setActivatorNodeRef } = useDraggable({
        id: currentCallUUID,
        disabled: isInternalCall,
        data: {
            phone,
            uuid: currentCallUUID,
            caller_id_name: !isUnknown ? person.name : unknown.name || "Unknown",
            source: "office-view-call-box"
        }
    });
    const dragHandle = <DragHandle ref={setActivatorNodeRef} isDragging={isDragging} {...listeners} {...attributes} disabled={isInternalCall} />;
    const isArchived = person?.archived;
    return (
        <div>
            <div ref={setNodeRef} data-uuid={currentCallUUID}>
                <Grid columns='equal' verticalAlign='middle'>
                    {isCallNotIdle && (
                        <Grid.Column width={1} textAlign='left'>
                            {dragHandle}
                        </Grid.Column>
                    )}
                    <Grid.Column>
                        <OfficeViewCallerDetails 
                            callState={callState}
                            person={person}
                            onRemovePerson={onRemovePerson}
                            unknown={unknown}
                            isInternalCall={isInternalCall}
                            currentCall={currentCall}
                            currentCallUUID={currentCallUUID}
                            isArchived={isArchived}
                        />
                    </Grid.Column>
                </Grid>
            </div>
            <Portal open={isDragging}>
                <DragOverlay>
                    <DraggableCall 
                        dragHandle={dragHandle}
                        personDisplay={!isUnknown ? person.name : unknown.name || "Unknown"}
                        isRinging={isCallRinging}
                        isHeld={isCallHeld}
                        duration={isCallActive && PhoneUtil.timeTicker(currentCall?.connected && currentTime > 0 ? currentTime - currentCall?.connected + (currentCall?.drift || 0) : 0 )}
                    />
                </DragOverlay>
            </Portal>
            {!isUnknown &&
                <div style={{ display: 'flex', gap: 15, marginTop: 15, marginLeft: 10 }}>
                    {isPhoneSystemSupported ? (
                        <ButtonLink
                            onClick={onClickToDial}
                            disabled={isCallNotIdle || !hasPhone || isArchived || callSuspended}
                        >
                            {!callSuspended ? (
                                <Icon
                                    className="fa-solid fa-phone OfficeView__icons"
                                    size={"large"}
                                    disabled={isCallNotIdle || !hasPhone}
                                    primary
                                />
                            ) : (
                                <BannedIcon 
                                    iconName={'fas fa-phone'}
                                    size='large'
                                />
                            )}
                        </ButtonLink>
                    ) : (
                        <Popup
                            className="AddForm"
                            trigger={
                                <ButtonLink>
                                    <Icon
                                        className="fa-solid fa-phone OfficeView__icons"
                                        size={"large"}
                                        primary
                                        disabled={isCallNotIdle || !hasPhone}
                                    />
                                </ButtonLink>
                            }
                            content={() =>
                                logActivityForm(
                                    {
                                        id: person?.contact_id || person?.account_id,
                                    },
                                    false,
                                    person?.contact_id ? true : false,
                                    () => setIsCallActivityFormOpen(false),
                                    true
                                )
                            }
                            closeOnDocumentClick={false}
                            open={isCallActivityFormOpen}
                            onClose={() => setIsCallActivityFormOpen(false)}
                            onOpen={() => setIsCallActivityFormOpen(true)}
                            on="click"
                            position="bottom left"
                            pinned
                            basic
                            wide
                        />
                    )}
                    
                    {!isInternalCall && hasIntegration ? (
                        <ButtonLink onClick={onEmailClick} disabled={isEmailDisabled || isArchived}>
                            <Icon className='fa-solid fa-envelope OfficeView__icons' size={'large'} />
                        </ButtonLink>
                    ) : (
                        <Popup
                            className="AddForm"
                            trigger={
                                <ButtonLink onClick={() => setIsLogActivityModalOpen(true)} disabled={isEmailDisabled || isArchived}>
                                    <Icon className='fa-solid fa-envelope OfficeView__icons' size={'large'} />
                                </ButtonLink>
                            }
                            content={() =>
                                logActivityForm(
                                    {
                                        id: person.contact_id,
                                        email: {
                                            email: person?.contact_id ? person.contact_details.email : person?.account_details?.email,
                                            main: true,
                                        },
                                    },
                                    true,
                                    false,
                                    () => setIsLogActivityModalOpen(false)
                                )
                            }
                            open={isLogActivityModalOpen}
                            on="click"
                            position="bottom left"
                            offset={[25, -125]}
                            pinned
                            basic
                            wide
                        />
                    )}
                    {!isInternalCall && (person?.contact_id && !isUnknown) &&(
                        <>
                            <ButtonLink onClick={onTaskClick} disabled={isArchived}>
                                <Icon className='fa-solid fa-clipboard-check OfficeView__icons' size={'large'} />
                            </ButtonLink>
                            <ButtonLink onClick={onEventClick} disabled={isArchived}>
                                <Icon className='fa-solid fa-calendar-day OfficeView__icons' size={'large'} />
                            </ButtonLink>
                            <ButtonLink onClick={onHotlistClick} disabled={isArchived}>
                                <Icon className={`${isInHotlist ? "fa-solid fa-fire-flame-curved OfficeView__icons--red" : "fa-solid fa-fire-flame OfficeView__icons"}`} size={'large'} />
                            </ButtonLink>
                            <ButtonLink onClick={onDirectCustomerClick} disabled={isArchived}>
                                <Icon className={`fas fa-crown ${direct_customer ? "OfficeView__icons--green" : "OfficeView__closeHeaderIcon"}`} size={'large'} />
                            </ButtonLink>
                        </>
                    )}
                </div>
            }
            {(isUnknown && !isInternalCall) && (
                <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_ADD} productPermission={PRODUCT_PERMISSIONS.RECORDS}>
                    <ButtonLink style={{ marginTop: 20 }} onClick={onNewContactClick}>
                            <Icon className='fa-solid fa-plus OfficeView__unknownAddIcon' circular />
                        <strong style={{ marginLeft: 5 }}>New/Existing Contact</strong>
                    </ButtonLink>
                </RestrictedView>

            )}
        </div>
    )
}

export default OfficeViewMiddleHeader;
